import {useState, useEffect, useMemo, Fragment} from 'react'
import {useNavigate} from 'react-router-dom'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MailboxIcon from '@mui/icons-material/MarkunreadMailbox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import Invitation from './Invitation'
import Modal from '../../shared/Modal'
import Table from '../../shared/Table'

import {connect} from '../../../state/hooks/tools'
import {formatDate} from '../../../lib/data'
import {readEndpoint, createResource} from '../../../lib/asyncMethods'
import {INVITES} from '../../../state/actionTypes'

import './invitations.scss'

const COLUMNS = [
  {
    width: 100,
    headerName: 'First Name',
    field: 'firstName'
  },
  {
    width: 100,
    headerName: 'Last Name',
    field: 'lastName'
  },
  {
    width: 190,
    headerName: 'Email',
    field: 'email'
  },
  {
    width: 130,
    headerName: 'Status',
    field: 'status',
    renderCell: params => <Chip label={params.value} />
  },
  {
    width: 150,
    headerName: 'Sent At',
    field: 'sentAt',
    renderCell: params => (
      <span>{params.value && formatDate(params.value)}</span>
    )
  },
  {
    width: 130,
    headerName: 'Created At',
    field: 'createdAt',
    renderCell: params => (
      <span>{formatDate(params.value)}</span>
    )
  }
]

const Invitations = props => {
  const navigate = useNavigate()
  const [showModal, setShowModal] = useState(false)
  const [records, setRecords] = useState(props.invites || [])
  const [rowCount, setRowCount] = useState(props.invitesMeta?.count || 0)
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 12})

  useEffect(() => {
    const {page, pageSize} = paginationModel
    const currentPage = page + 1

    props.readEndpoint(`invites?page=${currentPage}&size=${pageSize}`).then(response => {
      if (response.success) {
        setRecords(response.data)
        setRowCount(response.meta.count)
      }
    })
  }, [paginationModel])

  const handleRowClick = row => {
    navigate(row.id)
  }

  const handleSubmit = data => {
    props.createResource({endpoint: 'invites', attributes: data}).then(response => {
      if (response.success) {
        setRecords([...records, response.data])
        setRowCount(prevCount => prevCount + 1)
      }

      toggleModal()
    })
  }

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  const actions = useMemo(() => ({
    width: 100,
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    getActions: params => ([
      <IconButton onClick={() => handleRowClick(params)} color='primary'>
        <VisibilityIcon />
      </IconButton>
    ])
  }), [])

  return (
    <Fragment>
      <Stack className='invitations'>
        <Stack sx={{flexDirection: 'row', justifyContent: 'space-between'}}>
          <Stack sx={{flexDirection: 'row', marginBottom: '15px'}}>
            <MailboxIcon fontSize='large' />
            <Typography variant='h5'>Invitations</Typography>
          </Stack>
          <Button onClick={toggleModal}>New Invitation</Button>
        </Stack>
        <Divider />
        <Table
          records={records}
          columns={[...COLUMNS, actions]}
          height={700}
          handleRowClick={handleRowClick}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          isLoading={props.loading}
        />
      </Stack>
      <Modal
        open={showModal}
        handleClose={toggleModal}
        disabled={props.loading}
        subHeading='Information about the user you intend to invite. Please make sure the information is correct before submitting.'
        buttonText='Done'
        title='Invitation'>
        <Invitation handleSubmit={handleSubmit} />
      </Modal>
    </Fragment>
  )
}

const mapStateToProps = ({invites}) => ({...invites})

const actionCreators = ({invites}) => ({readEndpoint: readEndpoint(invites, INVITES), createResource: createResource(invites, INVITES)})

export default connect(mapStateToProps, actionCreators)(Invitations)
